// Inline "log an activity" card for related-list panels. Covers calls, notes, LinkedIn,
// and historical emails — a lightweight way to record a touchpoint that already happened.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

type Kind = "CALL" | "EMAIL" | "LINKEDIN_MESSAGE" | "NOTE";

interface Props {
  contactId?: string;
  accountId?: string;
  dealId?: string;
  campaignId?: string;
  /** Restrict the type selector (e.g. lock to EMAIL on the Emails tab). */
  kinds?: Kind[];
  /** Submit-button label override. */
  submitLabel?: string;
  invalidateQueryKeys?: unknown[][];
}

const ALL_KINDS: { value: Kind; label: string }[] = [
  { value: "CALL",             label: "Call" },
  { value: "EMAIL",            label: "Email" },
  { value: "LINKEDIN_MESSAGE", label: "LinkedIn message" },
  { value: "NOTE",             label: "Note" },
];

export function QuickLogActivity({
  contactId, accountId, dealId, campaignId, kinds, submitLabel, invalidateQueryKeys = [],
}: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const allowed = kinds ? ALL_KINDS.filter((k) => kinds.includes(k.value)) : ALL_KINDS;
  const [type, setType] = useState<Kind>(allowed[0].value);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [outcome, setOutcome] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      // Email logging goes through the dedicated graph/log-email endpoint so it
      // doubles as a "I sent this from Outlook" record.
      if (type === "EMAIL") {
        await api.post("/graph/log-email", {
          subject, body, direction: "outbound",
          contactId, accountId, dealId,
        });
        return;
      }
      await api.post("/activities", {
        type, subject, body, outcome: outcome || undefined,
        contactId, accountId, dealId, campaignId,
        completedAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      setSubject(""); setBody(""); setOutcome(""); setOpen(false); setError(null);
      invalidateQueryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to log activity."),
  });

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="mr-1 h-4 w-4"/>{submitLabel ?? "Log activity"}
      </Button>
    );
  }

  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium">{submitLabel ?? "Log activity"}</div>
        <button onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4"/>
        </button>
      </div>
      <div className="grid gap-2">
        {allowed.length > 1 && (
          <Select value={type} onChange={(e) => setType(e.target.value as Kind)}>
            {allowed.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        )}
        <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea
          className="min-h-[80px] w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder={type === "EMAIL" ? "Body / summary of the email sent" : "Details (optional)"}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {type !== "EMAIL" && type !== "NOTE" && (
          <Input placeholder="Outcome (e.g. left voicemail, booked follow-up)" value={outcome} onChange={(e) => setOutcome(e.target.value)} />
        )}
        <div className="flex justify-end">
          <Button onClick={() => mut.mutate()} disabled={!subject || mut.isPending}>
            {mut.isPending ? "Saving…" : (submitLabel ?? "Log")}
          </Button>
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
